/**
 * Route-level code splitting (§13.6). Every screen is a lazy chunk behind the layout-matching RouteLoading
 * fallback; the landing chunk is the only one counted in the initial-JS budget (scripts/check-bundle.mjs).
 *
 * Usage in a feature folder's `index.tsx`:
 *   const EnterPage = lazyRoute(() => import('./EnterPage'));
 *   export const entryRoutes: RouteObject[] = [{ path: 'enter', element: <EnterPage /> }];
 */
import { lazy, Suspense, type ComponentType } from 'react';
import { RouteLoading } from '../components/RouteLoading';

type Loader<P> = () => Promise<{ default: ComponentType<P> }>;

export function lazyRoute<P extends object = Record<string, never>>(
  loader: Loader<P>,
  fallback: 'page' | 'lottery' = 'page',
): ComponentType<P> {
  const Lazy = lazy(loader);
  const Wrapped = (props: P) => (
    <Suspense fallback={<RouteLoading shape={fallback} />}>
      <Lazy {...props} />
    </Suspense>
  );
  Wrapped.displayName = 'LazyRoute';
  return Wrapped;
}
